export class AlHtmlMatch {
    name:string = '';
    start:number;
    end:number = -1;
    content:string = '';
    attributes:{[attribute:string]:string|boolean} = {};
    children: AlHtmlMatch[] = [];

    constructor( setStart:number = 0 ) {
        this.start = setStart;
    }

    /**
     * Get first child element of a given type
     */
    public first( childType:string ):AlHtmlMatch|undefined {
        return this.children.find( c => c.name === childType );
    }

    /**
     * Get all child elements of a given type
     */
    public all( childType:string ):AlHtmlMatch[] {
        return this.children.filter( c => c.name === childType );
    }

    /**
     * Coerces a given attribute into string form, with a default value
     */
    public attr( attribute:string, defaultValue?:string ):string {
        if ( attribute in this.attributes ) {
            if ( typeof( this.attributes[attribute] ) === 'boolean' ) {
                return this.attributes[attribute] ? "true" : "false";
            } else {
                return this.attributes[attribute] as string;
            }
        }
        return defaultValue || "";
    }

    /**
     * Coerces a given attribute into a boolean form, with a default value
     */
    public attrSwitch( attribute:string, defaultValue:boolean ):boolean {
        if ( attribute in this.attributes ) {
            if ( typeof( this.attributes[attribute] ) === 'boolean' ) {
                return this.attributes[attribute] as boolean;
            } else {
                return ( this.attributes[attribute] === 'false' || this.attributes[attribute] === "" ) ? false : true;
            }
        }
        return defaultValue;
    }
};

type ParseMode = "name"|"attributes"|"attribute"|"value"|"content"|null;

export class AlHtmlToJsonParser {
    public static reElement = /[a-zA-Z0-9\-:]/;
    public static reAttribute = /[a-zA-Z0-9\-_]/;
    public static reWhitespace = /[\s]/;
    constructor( public content:string, 
                 public offset:number = 0,
                 public prefix:string = "c:" ) {
    }

    public matchAll():AlHtmlMatch[] {
        let results:AlHtmlMatch[] = [];
        while ( 1 ) {
            const match = this.matchOne();
            if ( match ) {
                results.push( match );
            } else {
                break;
            }
        }
        return results;
    }

    public matchOne():AlHtmlMatch|null {
        let startOffset = this.content.indexOf( `<${this.prefix}`, this.offset );
        if ( startOffset === -1 ) {
            return null;
        }

        let match = this.extractElement( startOffset );
        if ( match ) {
            this.offset = match.end + 1;
        }
        return match;
    }

    public extractElement( index:number, depth:number = 0 ):AlHtmlMatch {
        let match = new AlHtmlMatch( index );
        if ( this.content[index] !== '<' ) {
            throw new Error(`Parsing error: offset ${index} of input data is not a '<' character` );
        }

        index++;

        let phrase:string       =   '';
        let test:string|RegExp  =   AlHtmlToJsonParser.reElement;
        let invert:boolean      =   false;
        let target:ParseMode    =   "name";
        let targetAttribute     =   '';
        let passby:number       =   0;

        function consume( setTarget:ParseMode,
                          setTest:string|RegExp,
                          setInvert:boolean = false ) {
            target = setTarget;
            test = setTest;
            invert = setInvert;
        }

        function skip( charactersToSkip:number = 1 ) {
            passby += charactersToSkip;
        }
        
        while ( index < this.content.length ) {
            let c = this.content[index];
            let characterMatches = typeof( test ) === 'string' ? c === test : test.test( c );
            if ( invert ) {
                characterMatches = characterMatches ? false : true;
            }
            if ( characterMatches ) {
                phrase += c;
            } else {
                let captured = phrase;
                phrase = "";
                if ( target === "name" ) {
                    match.name = captured;
                    consume( "attributes", AlHtmlToJsonParser.reWhitespace );
                } else if ( target === "attributes" ) {
                    if ( c === '>' ) {
                        consume("content", "<", true );
                    } else {
                        consume("attribute", AlHtmlToJsonParser.reAttribute );
                    }
                } else if ( target === "attribute" ) {
                    targetAttribute = captured;
                    if ( c === '=' ) {
                        let delimiter = this.content[index+1];
                        if ( delimiter === `'` || delimiter === `"` ) {
                            consume( "value", delimiter, true );
                            skip( 2 );
                            index++;
                        } else {
                            consume( "value", AlHtmlToJsonParser.reWhitespace, true );
                            skip( 1 );
                        }
                    } else {
                        match.attributes[targetAttribute] = true;
                        consume( "attributes", AlHtmlToJsonParser.reWhitespace );
                    }
                } else if ( target === "value" ) {
                    match.attributes[targetAttribute] = captured;
                    consume( "attributes", AlHtmlToJsonParser.reWhitespace );
                } else if ( target === "content" ) {
                    match.content += captured;
                    consume( null, AlHtmlToJsonParser.reWhitespace );
                }

                if ( c === '>' ) {
                    consume( "content", "<", true );
                } else if ( c === '<' ) {
                    if ( this.content[index+1] === '/' ) {
                        let closure = this.content.substring(index, index + match.name.length + 3 );
                        if ( closure !== `</${match.name}>` ) {
                            throw this.contextualError( index, `found an unexpected element closure that was not for '${match.name}'` );
                        }
                        match.end = index + closure.length;
                        break;
                    } else {
                        const childElement = this.extractElement( index, depth + 1 );
                        match.children.push( childElement );
                        index = childElement.end;
                    }
                } else if ( c === '/' && this.content[index + 1] === '>' ) {
                    match.end = index + 2;
                    break;
                } else if ( passby > 0 ) {
                    passby--;
                } else {
                    phrase += c;
                }
            }
            index++;
        }
        if ( index >= this.content.length ) {
            throw this.contextualError( match.start, `element '${match.name}' is unclosed` );
        }
        return match;
    }

    protected contextualError( offset:number, reason:string ):Error {
        let location = this.offsetToLocation( offset );
        return new Error( `Invalid markup at line ${location.row}, col ${location.column}: ${reason}` );
    }

    protected offsetToLocation( offset:number ):{ row:number, column:number } {
        let row = 1;
        let column = 1;
        for ( let i = 0; i < offset; i++ ) {
            if ( this.content[i] === "\n" ) {
                row++;
                column = 1;
            } else {
                column++;
            }
        }
        return { row, column };
    }
}
